import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { outlookService } from '@/lib/email/outlook';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';

export function OutlookCallback() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const hasHandled = useRef(false);
  const [status, setStatus] = useState('Processing authentication...');

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double-handling in React StrictMode
      if (hasHandled.current) return;
      hasHandled.current = true;

      console.log('[OutlookCallback] Starting callback handling...');
      console.log('[OutlookCallback] Current URL:', window.location.href);

      try {
        setStatus('Handling Microsoft response...');

        // Handle the redirect response from Microsoft
        const response = await outlookService.handleRedirectResponse();

        console.log('[OutlookCallback] Response:', response ? 'Got response' : 'No response');

        if (!response) {
          // No response means this isn't a redirect callback
          console.log('[OutlookCallback] No redirect response found');
          setStatus('No authentication response found');
          toast({
            title: "Error",
            description: "No authentication response received from Microsoft",
            variant: "destructive",
          });
          setTimeout(() => navigate('/settings/senders'), 2000);
          return;
        }

        setStatus('Saving account...');

        const workspaceId = localStorage.getItem('connecting_workspace_id');
        console.log('[OutlookCallback] Workspace ID:', workspaceId);

        if (!workspaceId) {
          throw new Error('No workspace ID found - please try connecting again');
        }

        // The response contains the access token from the redirect
        const accessToken = response.accessToken;
        const account = response.account;

        console.log('[OutlookCallback] Account:', account?.username);
        console.log('[OutlookCallback] Token received:', !!accessToken);
        console.log('[OutlookCallback] Scopes granted:', response.scopes);

        if (!accessToken || !account) {
          throw new Error('No access token or account in response');
        }

        // Get current user ID
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        // Extract refresh token from MSAL's internal localStorage cache
        // MSAL browser doesn't expose it on AuthenticationResult, but it's cached
        const refreshToken = account.homeAccountId
          ? outlookService.extractRefreshTokenFromCache(account.homeAccountId)
          : null;

        // Refuse to save if no refresh_token landed. Without it, the
        // server-side refresh path 401s an hour later and the user spends days
        // wondering why their reconnect didn't work. Leave the existing
        // provider row untouched so any previous good tokens are preserved.
        if (!refreshToken) {
          console.error('[OutlookCallback] No refresh token extracted from MSAL — refusing to save partial credentials');
          throw new Error(
            'Microsoft did not return a refresh token. Click Disconnect first, then Connect again — the existing consent was reused without minting fresh tokens.',
          );
        }

        console.log('[OutlookCallback] Refresh token extracted from MSAL cache');

        // Sanity-check the access token expiry too. If MSAL handed back an
        // already-expired access_token (we have seen this when consent is
        // reused), abort instead of writing a stale expires_at the processor
        // will then chase forever.
        const expiresOnDate = response.expiresOn ? new Date(response.expiresOn) : null;
        if (expiresOnDate && expiresOnDate.getTime() < Date.now()) {
          console.error('[OutlookCallback] expiresOn is in the past — Microsoft returned a stale cached access_token; refusing to save', expiresOnDate.toISOString());
          throw new Error(
            "Microsoft returned an already-expired access token. Click Disconnect first, then Connect again — your previous consent was reused instead of minting a fresh session.",
          );
        }

        // Step 1: upsert provider metadata WITHOUT tokens (clients cannot
        // write to the encrypted bytea columns).
        const { data: provider, error: dbError } = await supabase
          .from('email_providers')
          .upsert({
            type: 'outlook',
            email: account.username,
            workspace_id: workspaceId,
            user_id: user.id,
            expires_at: expiresOnDate ? expiresOnDate.toISOString() : null,
          }, {
            onConflict: 'workspace_id,type,email',
          })
          .select()
          .single();

        if (dbError) {
          console.error('[OutlookCallback] Database error:', dbError);
          throw dbError;
        }

        // Step 2: store tokens via SECURITY DEFINER RPC (encrypts).
        const { error: tokError } = await supabase.rpc('email_provider_set_tokens', {
          p_provider_id:   provider.id,
          p_access_token:  accessToken,
          p_refresh_token: refreshToken,
          p_expires_at:    expiresOnDate ? expiresOnDate.toISOString() : null,
        });
        if (tokError) {
          console.error('[OutlookCallback] email_provider_set_tokens failed:', tokError);
          throw tokError;
        }

        // Check if connecting for calendar — fetch calendar list if so
        const connectingProvider = localStorage.getItem('connecting_provider');
        const isCalendar = connectingProvider === 'outlook_calendar';

        if (isCalendar && provider) {
          setStatus('Fetching your calendars...');
          await fetchOutlookCalendarList(accessToken, provider.id, workspaceId, user.id);
        }

        // Clean up localStorage
        localStorage.removeItem('connecting_workspace_id');
        localStorage.removeItem('connecting_provider');

        const redirectTo = isCalendar ? '/settings/calendar' : '/settings/senders';
        console.log(`[OutlookCallback] Success! Redirecting to ${redirectTo}...`);
        setStatus('Connected successfully! Redirecting...');

        toast({
          description: "Outlook account connected successfully",
        });

        navigate(redirectTo);
      } catch (error) {
        console.error('[OutlookCallback] Error:', error);
        captureError(error as Error, { feature: 'outlook-oauth-callback' });
        setStatus('Error connecting account');
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to connect Outlook account",
          variant: "destructive",
        });
        // Still redirect to senders page so user can try again
        setTimeout(() => navigate('/settings/senders'), 2000);
      }
    };

    handleCallback();
  }, [navigate, toast]);

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        <p className="text-muted-foreground">{status}</p>
      </div>
    </div>
  );
}

async function fetchOutlookCalendarList(
  accessToken: string,
  providerId: string,
  workspaceId: string,
  userId: string,
) {
  const response = await fetch(
    'https://graph.microsoft.com/v1.0/me/calendars',
    { headers: { 'Authorization': `Bearer ${accessToken}` } },
  );
  if (!response.ok) {
    console.error('[OutlookCallback] Failed to fetch calendar list:', await response.text());
    return;
  }

  const data = await response.json();
  const calendars = (data.value || []).map((cal: any) => ({
    provider_id: providerId,
    workspace_id: workspaceId,
    user_id: userId,
    external_id: cal.id,
    name: cal.name || 'Calendar',
    color: cal.hexColor || null,
    is_primary: cal.isDefaultCalendar || false,
    is_selected: cal.isDefaultCalendar || false,
    access_role: cal.canEdit ? 'writer' : 'reader',
  }));

  if (calendars.length > 0) {
    const { error } = await supabase
      .from('calendar_calendars')
      .upsert(calendars, { onConflict: 'provider_id,external_id' });
    if (error) console.error('[OutlookCallback] Error saving calendars:', error);
  }
}
